/**
 * StandardizationTab — UI theo layout Excel "Thông tin qui chuẩn" (23 cột, header
 * 2 tầng group Before / Amount / After), nhưng CRUD persist vào bảng
 * `tlc_factory.po_standard` (4 field nhập tay):
 *   stack_before → Stack ID · deduct_rate → Deduct % · stack_after → Stack ID (std) · reason.
 * 19 cột quality / Receive NW là computed (chưa có nguồn DB) → để trống.
 *
 * Skill `/table-style`: FilterBar Search + per-column ▽ + Clear All; app-dataframe
 * (white rows + gray hover); freeze cột Stack ID (app-sticky-col); row-select toggle.
 */
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useMemo, useState, type CSSProperties } from 'react'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
} from '@/components/dataList'
import {
  useCreateStandardizationMutation,
  useDeleteStandardizationMutation,
  useGetBatchReportPersistQuery,
  useGetPoStacksQuery,
  useGetStandardizationRowsQuery,
  useUpdateStandardizationMutation,
  type StandardizationRow,
  type StandardizationSaveBody,
} from '@/store/api/factoryApi'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import ResizeHandle from '@/components/ResizeHandle'
import { fmtPercentScaled, fmtPlain } from './formatters'
import { NumText, PctText } from './reportUi'
import { COLORS } from './styleTokens'
import StandardizationFormDialog from './StandardizationFormDialog'

interface Props {
  factoryCode: string
  poNo: string
  canEdit?: boolean
}

type StdGroup = 'before' | 'amount' | 'after'

interface StdColumn {
  key: string
  label: string
  width: number
  group: StdGroup
  kind: 'text' | 'pct' | 'mt' | 'plain'
}

/** Freeze cột định danh = Stack ID (skill §7.6). */
const FROZEN_KEY = 'stackId'

// 23 cột theo Excel A2:W2. Chỉ stackId/deductPct/stackIdAfter/reason có data
// (map từ po_standard); còn lại computed → để trống.
// Width = vừa đủ load text: max(header label nowrap + ▽ filter icon, data thực).
// Data dài nhất (PO PRO-TLBL-25005): GRN 15 ký tự "GRN-TLBL-250366", Stack ID 14.
const STD_COLUMNS: StdColumn[] = [
  { key: 'stackId', label: 'Stack ID', width: 150, group: 'before', kind: 'text' },
  { key: 'grnBefore', label: 'GRN', width: 150, group: 'before', kind: 'text' },
  { key: 'itemCode', label: 'Item Code', width: 104, group: 'before', kind: 'text' },
  { key: 'receiveNw', label: 'Receive NW (total by Item)', width: 200, group: 'before', kind: 'mt' },
  { key: 'deductPct', label: 'Deduct %', width: 100, group: 'amount', kind: 'pct' },
  { key: 'deductNw', label: 'Deduct NW', width: 108, group: 'amount', kind: 'mt' },
  { key: 'itemAfter', label: 'Item (std)', width: 110, group: 'after', kind: 'text' },
  { key: 'stackIdAfter', label: 'Stack ID (std)', width: 160, group: 'after', kind: 'text' },
  { key: 'grnAfter', label: 'GRN (std)', width: 150, group: 'after', kind: 'text' },
  { key: 'reason', label: 'Reason', width: 200, group: 'after', kind: 'text' },
  { key: 'stdBlacks', label: 'Std Blacks', width: 108, group: 'after', kind: 'plain' },
  { key: 'stdBrokens', label: 'Std Brokens', width: 118, group: 'after', kind: 'plain' },
  { key: 'stdFm', label: 'Std FM', width: 90, group: 'after', kind: 'plain' },
  { key: 'blacksAfter', label: 'Blacks (after)', width: 138, group: 'after', kind: 'plain' },
  { key: 'brokensAfter', label: 'Brokens (after)', width: 146, group: 'after', kind: 'plain' },
  { key: 'fmAfter', label: 'FM (after)', width: 110, group: 'after', kind: 'plain' },
  { key: 'stdS18', label: 'Std S18', width: 94, group: 'after', kind: 'plain' },
  { key: 's18After', label: 'S18 (after)', width: 118, group: 'after', kind: 'plain' },
  { key: 'stdS16', label: 'Std S16', width: 94, group: 'after', kind: 'plain' },
  { key: 's16After', label: 'S16 (after)', width: 118, group: 'after', kind: 'plain' },
  { key: 'stdS13', label: 'Std S13', width: 94, group: 'after', kind: 'plain' },
  { key: 's13After', label: 'S13 (after)', width: 118, group: 'after', kind: 'plain' },
  { key: 'stdBrown', label: 'Std Brown', width: 108, group: 'after', kind: 'plain' },
  { key: 'brownAfter', label: 'Brown (after)', width: 138, group: 'after', kind: 'plain' },
]

// Phân biệt block bằng MÀU NỀN header (thay cho dòng group header tier-1 đã bỏ).
const GROUP_BG: Record<StdGroup, string> = {
  before: '#dce8e6', // teal tint
  amount: '#f3e3cb', // copper / sand tint
  after: '#e3ecde',  // forest tint
}

type DisplayRow = Record<string, unknown> & { _id: number }

/** po_standard row → display row theo layout Excel (4 field có data, còn lại trống). */
function toDisplayRow(r: StandardizationRow, poNo: string): DisplayRow {
  return {
    _id: r.id,
    batch: poNo,
    stackId: r.stackBefore ?? '',
    grnBefore: r.grnBefore ?? '',
    itemCode: r.stackBeforeItem ?? '',     // computed (view)
    receiveNw: r.stackBeforeMt,            // computed (view)
    deductPct: r.deductRate, // fraction (1 = 100%)
    deductNw: r.deductValue,               // computed (view)
    itemAfter: r.stackAfterItem ?? '',     // computed (view)
    stackIdAfter: r.stackAfter ?? '',
    grnAfter: r.grnAfter ?? '',
    reason: r.reason ?? '',
    // Computed quality (std threshold + after) — các cột sau Reason.
    stdBlacks: r.blackStd,
    stdBrokens: r.brokensStd,
    stdFm: r.fmStd,
    stdS18: r.s18Std,
    stdS16: r.s16Std,
    stdS13: r.s13Std,
    stdBrown: r.brownStd,
    blacksAfter: r.blackAfterStd,
    brokensAfter: r.brokensAfterStd,
    fmAfter: r.fmAfterStd,
    s18After: r.s18AfterStd,
    s16After: r.s16AfterStd,
    s13After: r.s13AfterStd,
    brownAfter: r.brownAfterStd,
  }
}

/** String để tooltip (title) — format Access-parity. */
function fmtCell(v: unknown, kind: StdColumn['kind']): string {
  if (v == null || v === '') return ''
  switch (kind) {
    case 'pct': return fmtPercentScaled(Number(v), 2)
    case 'mt': return fmtPlain(Number(v), 2)
    case 'plain': return fmtPlain(Number(v), 2)
    default: return String(v)
  }
}

/** Render ô số theo style kế toán report (NumText/PctText: căn phải tabular-nums,
 *  số âm → đỏ ngoặc) — đồng nhất tab BOM & Budget/Actual. Text → chuỗi thường. */
function renderCell(v: unknown, kind: StdColumn['kind']): React.ReactNode {
  if (kind === 'text') return v == null ? '' : String(v)
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  switch (kind) {
    case 'pct': return <PctText frac={n} d={2} />
    case 'mt': return <NumText n={n} d={2} />
    case 'plain': return <NumText n={n} d={2} />
    default: return String(v)
  }
}

function asFilterColumn(col: StdColumn) {
  return { key: col.key, label: col.label, kind: col.kind === 'text' ? 'text' : 'number' }
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; row: StandardizationRow }

export default function StandardizationTab({ factoryCode, poNo, canEdit = false }: Props) {
  const snack = useSnackbar()
  const skip = !factoryCode || !poNo.trim()
  const { data, isFetching } = useGetStandardizationRowsQuery({ factoryCode, poNo: poNo.trim() }, { skip })
  // Finished → khoá CRUD (Phương án A): không cho New/Edit/Delete sau khi PO đóng.
  const { data: persist } = useGetBatchReportPersistQuery({ factoryCode, poNo: poNo.trim() }, { skip })
  const editable = canEdit && !persist?.isFinished
  const srcRows = useMemo(() => data?.items ?? [], [data])
  const baseRows = useMemo<DisplayRow[]>(() => srcRows.map((r) => toDisplayRow(r, poNo)), [srcRows, poNo])

  // Stack + GRN options — (stack, GRN) của PO (view v_batchreport_receive).
  // stackOptions = stack ID distinct; grnByStack = GRN khả dụng theo từng stack
  // (1 stack có thể nhiều GRN) → combo GRN trong form lọc theo stack đã chọn.
  const { data: stacksData } = useGetPoStacksQuery({ factoryCode, poNo: poNo.trim() }, { skip })
  const stackOptions = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const s of stacksData?.items ?? []) {
      if (s.stackId && !seen.has(s.stackId)) { seen.add(s.stackId); out.push(s.stackId) }
    }
    return out
  }, [stacksData])
  const grnByStack = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const s of stacksData?.items ?? []) {
      if (!s.stackId || !s.grnId) continue
      ;(map[s.stackId] ??= []).push(s.grnId)
    }
    return map
  }, [stacksData])
  // mt theo (stack, GRN) — suggest "Receive NW (Mt)" trong form khi chọn Stack-GRN.
  const mtByStackGrn = useMemo(() => {
    const map: Record<string, number | null> = {}
    for (const s of stacksData?.items ?? []) {
      if (!s.stackId) continue
      map[`${s.stackId} ${s.grnId ?? ''}`] = s.mt
    }
    return map
  }, [stacksData])

  const [createStd, { isLoading: creating }] = useCreateStandardizationMutation()
  const [updateStd, { isLoading: updating }] = useUpdateStandardizationMutation()
  const [deleteStd] = useDeleteStandardizationMutation()
  const saving = creating || updating

  const { selectedId, toggleSelect, clearSelection } = useRowSelection<number>()
  const selectedSrc = useMemo(() => srcRows.find((r) => r.id === selectedId) ?? null, [srcRows, selectedId])
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [pendingDelete, setPendingDelete] = useState<StandardizationRow | null>(null)

  // --- Filter state ---
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAll = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  const filteredRows = useMemo(
    () => baseRows.filter((r) =>
      rowMatchesSearch(r, searchTerm, STD_COLUMNS) && rowMatchesAllColumnFilters(r, columnFilters.filters),
    ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const popoverColumn = useMemo(
    () => (colPopover ? STD_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(asFilterColumn(popoverColumn), baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, colPopover.key) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  // Resizable column width (skill /table-style) — persist per-user qua user_preferences.
  const { getWidth, startResize } = useColumnWidths('factory.standardization')
  const colW = (c: StdColumn) => getWidth(c.key, c.width) ?? c.width

  // Total row — SUM cho cột khối lượng (Receive NW / Deduct NW); cột khác để trống.
  const totals = useMemo(() => {
    const t: Record<string, number> = {}
    for (const col of STD_COLUMNS) {
      if (col.kind === 'mt') {
        t[col.key] = filteredRows.reduce((s, r) => s + (Number(r[col.key]) || 0), 0)
      }
    }
    return t
  }, [filteredRows])

  const handleSave = async (body: StandardizationSaveBody) => {
    try {
      if (dialog.kind === 'edit') {
        await updateStd({ factoryCode, poNo: poNo.trim(), id: dialog.row.id, body }).unwrap()
      } else {
        await createStd({ factoryCode, poNo: poNo.trim(), body }).unwrap()
      }
      setDialog({ kind: 'closed' })
    } catch {
      snack.error('Save failed.')
    }
  }
  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteStd({ factoryCode, poNo: poNo.trim(), id: pendingDelete.id }).unwrap()
      clearSelection()
    } catch {
      snack.error('Delete failed.')
    }
    setPendingDelete(null)
  }

  return (
    <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAll}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              disabled={!editable || skip} onClick={() => setDialog({ kind: 'new' })} sx={filterBarBtnSx}>
              New
            </Button>
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              disabled={!editable || !selectedSrc} onClick={() => selectedSrc && setDialog({ kind: 'edit', row: selectedSrc })} sx={filterBarBtnSx}>
              Edit
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              disabled={!editable || !selectedSrc} onClick={() => selectedSrc && setPendingDelete(selectedSrc)} sx={filterBarBtnSx}>
              Delete
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => searchPopover.open('search', e)} />
      </FilterBar>

      {/* Fill full chiều cao content (như list page /baoloc/stack). `!important`
          để thắng override `.app-dataframe { height:auto; overflow:visible }` của
          vùng content report (override đó dành cho các tab dài auto-scroll). */}
      <Box
        className="app-dataframe"
        sx={{
          flex: 1,
          minHeight: 0,
          height: 'auto !important',
          maxHeight: 'none !important',
          overflow: 'hidden !important',
        }}
      >
        <Box sx={{ flex: '1 1 auto', minHeight: 0, height: '100%', overflow: 'auto', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, flex: '0 0 auto' }}>
            {/* colgroup định nghĩa width cột — cần thiết vì header tầng 1 là cell
                colSpan (không width) nên tableLayout:fixed không suy được width từ
                header. Thiếu colgroup → cột bị bóp + group label bị che chữ. */}
            <colgroup>
              {STD_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: colW(c) }} />
              ))}
            </colgroup>
            <TableHead>
              {/* Column header + ▽ filter. Phân biệt block qua MÀU NỀN (GROUP_BG),
                  KHÔNG còn dòng group header tầng 1. */}
              <TableRow>
                {STD_COLUMNS.map((col) => {
                  const isFrozen = col.key === FROZEN_KEY
                  const bg = GROUP_BG[col.group]
                  const w = colW(col)
                  const style: CSSProperties = {
                    width: w, minWidth: w, maxWidth: w,
                    position: 'sticky', top: 0, zIndex: isFrozen ? 7 : 5,
                    backgroundColor: bg, backgroundImage: 'none',
                  }
                  if (isFrozen) {
                    style.left = 0
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.kind === 'text' ? 'left' : 'right'}
                      className="app-table-header-cell"
                      style={style}
                      // Label + nút ▽ filter cùng 1 dòng (nowrap) + grip kéo giãn width.
                      sx={{ whiteSpace: 'nowrap', position: 'relative' }}
                      title={col.label}
                    >
                      {col.label}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(col.key)}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => {
                  const id = row._id
                  return (
                    <TableRow
                      key={id}
                      hover
                      selected={id === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleSelect(id)}
                    >
                      {STD_COLUMNS.map((col) => {
                        const raw = row[col.key]
                        const title = fmtCell(raw, col.kind)
                        const isFrozen = col.key === FROZEN_KEY
                        const w = colW(col)
                        return (
                          <TableCell
                            key={col.key}
                            align={col.kind === 'text' ? 'left' : 'right'}
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={{ width: w, minWidth: w, maxWidth: w }}
                            sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={title}
                          >
                            <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }}>
                              {renderCell(raw, col.kind)}
                            </Box>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
            </TableBody>
            {filteredRows.length > 0 ? (
              <tfoot>
                {/* Total row — sticky bottom; SUM cột khối lượng (Receive NW / Deduct NW). */}
                <TableRow>
                  {STD_COLUMNS.map((col, index) => {
                    const isFrozen = col.key === FROZEN_KEY
                    const w = colW(col)
                    const hasTotal = col.kind === 'mt'
                    return (
                      <TableCell
                        key={col.key}
                        align={col.kind === 'text' ? 'left' : 'right'}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={{
                          width: w, minWidth: w, maxWidth: w,
                          position: 'sticky', bottom: 0,
                          ...(isFrozen ? { left: 0, zIndex: 4 } : { zIndex: 2 }),
                        }}
                        sx={{
                          fontWeight: 700, whiteSpace: 'nowrap', py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : hasTotal ? <NumText n={totals[col.key]} d={2} /> : ''}
                      </TableCell>
                    )
                  })}
                </TableRow>
              </tfoot>
            ) : null}
          </Table>
          {filteredRows.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textSubtle }}>
                {skip ? 'Select a PO to view standardization rows.' : isFetching ? 'Loading…' : 'No standardization rows for this PO.'}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder="Search standardization rows…"
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />

      <StandardizationFormDialog
        open={dialog.kind === 'new' || dialog.kind === 'edit'}
        mode={dialog.kind === 'edit' ? 'edit' : 'new'}
        row={dialog.kind === 'edit' ? dialog.row : null}
        saving={saving}
        stackOptions={stackOptions}
        grnByStack={grnByStack}
        mtByStackGrn={mtByStackGrn}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Delete standardization row?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            Stack ID <b>{pendingDelete?.stackBefore}</b> will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPendingDelete(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button size="small" color="error" variant="contained" onClick={confirmDelete} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
